// ═══════════════════════════════════════════════════════════════════════════════════
// [FAMLA-APP-COMPLETE] BRANDING CSS INJECTOR - INJECTION DYNAMIQUE CSS
// ═══════════════════════════════════════════════════════════════════════════════════
//
// [RÔLE] Injecte les variables CSS personnalisées selon le branding
// [OBJECTIF] Appliquer les couleurs personnalisées sans recharger la page
// [ARCHITECTURE] Manipulation DOM pour style tag + titre
//
// ═══════════════════════════════════════════════════════════════════════════════════

import { TenantBrandingDerived } from '../services/config';

const BRANDING_STYLE_ID = 'famla-custom-branding-styles';
const ORIGINAL_FAVICON_HREF = '/favicon.ico';
const ORIGINAL_TITLE = 'Famla';

/**
 * Injecte les variables CSS personnalisées pour le branding tenant
 */
export function injectBrandingCss(branding: TenantBrandingDerived): void {
    if (!branding.enabled) {
        removeBrandingCss();
        return;
    }

    const existingStyle = document.getElementById(BRANDING_STYLE_ID);
    if (existingStyle) {
        existingStyle.remove();
    }

    // Convertir hex en RGB pour les variantes avec opacity
    const hexToRgb = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return '0, 217, 89';
        return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
    };
    
    const rgb = hexToRgb(branding.primaryColor);
    const hoverColor = branding.primaryColor + 'dd'; // Slightly darker

    const styleTag = document.createElement('style');
    styleTag.id = BRANDING_STYLE_ID;
    styleTag.textContent = `
        /* ═══════════════════════════════════════════════════════════════════════════════
           WHITE LABELLING - TENANT BRANDING CSS OVERRIDES
           Primary Color: ${branding.primaryColor}
           RGB: ${rgb}
           ═══════════════════════════════════════════════════════════════════════════════ */
        
        :root {
            /* BOOTSTRAP THEME OVERRIDES */
            --bs-theme: ${branding.primaryColor} !important;
            --bs-theme-rgb: ${rgb} !important;
            
            /* TENANT CORE VARIABLES */
            --tenant-primary: ${branding.primaryColor} !important;
            --tenant-primary-rgb: ${rgb} !important;
            --tenant-primary-hover: ${hoverColor} !important;
            --tenant-secondary: ${branding.primaryColor} !important;
            --tenant-secondary-rgb: ${rgb} !important;
            
            /* FAMLA BRAND OVERRIDES */
            --branding-primary-color: ${branding.primaryColor} !important;
            --branding-gradient: ${branding.gradient} !important;
            --famla-teal: ${branding.primaryColor} !important;
            --famla-blue: ${branding.primaryColor} !important;
            --famla-gradient: ${branding.gradient} !important;
            --famla-accent: ${branding.primaryColor} !important;
            --famla-accent-hover: ${hoverColor} !important;
            --famla-accent-soft: rgba(${rgb}, 0.14) !important;
            --famla-accent-border: rgba(${rgb}, 0.35) !important;
            --famla-accent-rgb: ${rgb} !important;
            
            /* ACCENT OVERRIDES */
            --accent: ${branding.primaryColor} !important;
            --accent-2: ${branding.primaryColor} !important;
            --accent-primary: ${branding.primaryColor} !important;
            --accent-secondary: ${branding.primaryColor} !important;
            --accent-dim: rgba(${rgb}, 0.12) !important;
            --accent-hover: rgba(${rgb}, 0.20) !important;
            
            /* STATUS OVERRIDES */
            --primary: ${branding.primaryColor} !important;
            --success: ${branding.primaryColor} !important;
            --success-dim: rgba(${rgb}, 0.12) !important;
            
            /* BORDER OVERRIDES */
            --border-primary: rgba(${rgb}, 0.15) !important;
            --border-hover: rgba(${rgb}, 0.25) !important;
        }
        
        /* Header specific overrides */
        .hdr-top {
            --famla-accent: ${branding.primaryColor} !important;
            --famla-accent-hover: ${branding.primaryColor}dd !important;
            --famla-accent-soft: rgba(${rgb}, 0.14) !important;
            --famla-accent-border: rgba(${rgb}, 0.35) !important;
        }
        
        /* Theme overrides */
        [data-theme="dark"], [data-theme="light"], .dark-theme, .light-theme {
            /* BOOTSTRAP THEME OVERRIDES */
            --bs-theme: ${branding.primaryColor} !important;
            --bs-theme-rgb: ${rgb} !important;
            
            /* TENANT CORE VARIABLES */
            --tenant-primary: ${branding.primaryColor} !important;
            --tenant-primary-rgb: ${rgb} !important;
            --tenant-primary-hover: ${hoverColor} !important;
            --tenant-secondary: ${branding.primaryColor} !important;
            --tenant-secondary-rgb: ${rgb} !important;
            
            /* FAMLA BRAND OVERRIDES */
            --famla-teal: ${branding.primaryColor} !important;
            --famla-blue: ${branding.primaryColor} !important;
            --famla-gradient: ${branding.gradient} !important;
            --famla-accent: ${branding.primaryColor} !important;
            --famla-accent-hover: ${hoverColor} !important;
            --famla-accent-soft: rgba(${rgb}, 0.14) !important;
            --famla-accent-border: rgba(${rgb}, 0.35) !important;
            --famla-accent-rgb: ${rgb} !important;
            
            /* ACCENT OVERRIDES */
            --accent: ${branding.primaryColor} !important;
            --accent-2: ${branding.primaryColor} !important;
            --accent-primary: ${branding.primaryColor} !important;
            --accent-secondary: ${branding.primaryColor} !important;
            --accent-dim: rgba(${rgb}, 0.12) !important;
            --accent-hover: rgba(${rgb}, 0.20) !important;
            
            /* STATUS OVERRIDES */
            --primary: ${branding.primaryColor} !important;
            --success: ${branding.primaryColor} !important;
            --success-dim: rgba(${rgb}, 0.12) !important;
            
            /* BORDER OVERRIDES */
            --border-primary: rgba(${rgb}, 0.15) !important;
            --border-hover: rgba(${rgb}, 0.25) !important;
        }
        
        /* Direct element overrides */
        .hdr-btn-new {
            background: ${branding.primaryColor} !important;
        }
        
        .hdr-btn-new:hover {
            background: ${branding.primaryColor}dd !important;
        }
        
        .hdr-avatar {
            background: rgba(${rgb}, 0.14) !important;
            border-color: rgba(${rgb}, 0.35) !important;
            color: ${branding.primaryColor} !important;
        }
        
        .modern-btn-primary {
            background: ${branding.gradient} !important;
        }

        .modal .btn-primary,
        .modal .btn-theme {
            --bs-btn-bg: ${branding.primaryColor} !important;
            --bs-btn-border-color: ${branding.primaryColor} !important;
            --bs-btn-hover-bg: ${hoverColor} !important;
            --bs-btn-hover-border-color: ${hoverColor} !important;
            --bs-btn-active-bg: ${hoverColor} !important;
            --bs-btn-active-border-color: ${hoverColor} !important;
            background-color: ${branding.primaryColor} !important;
            border-color: ${branding.primaryColor} !important;
        }

        .modal .btn-primary:hover,
        .modal .btn-theme:hover {
            background-color: ${hoverColor} !important;
            border-color: ${hoverColor} !important;
        }

        .force-map-gen-modal .modal-header {
            border-bottom-color: rgba(${rgb}, 0.55) !important;
        }

        .force-map-gen-modal .modal-title .tenant-accent-icon {
            color: ${branding.primaryColor} !important;
        }
        
        .modern-progress-fill {
            background: ${branding.gradient} !important;
        }
        
        .modern-section-link {
            color: ${branding.primaryColor} !important;
        }
        
        .modern-sidebar-item.active {
            background: rgba(${rgb}, 0.12) !important;
            color: ${branding.primaryColor} !important;
        }
        
        .modern-badge-accent {
            background: rgba(${rgb}, 0.12) !important;
            color: ${branding.primaryColor} !important;
        }
        
        .modern-text-gradient {
            background: ${branding.gradient} !important;
            background-clip: text !important;
            -webkit-background-clip: text !important;
        }
        
        /* Interview/Collect shell header overrides */
        .interview-redesign-shell-header,
        .collect-ws-header,
        .pc-header-chrome {
            --famla-accent: ${branding.primaryColor} !important;
            --famla-accent-hover: ${branding.primaryColor}dd !important;
            --famla-accent-soft: rgba(${rgb}, 0.14) !important;
            --famla-accent-border: rgba(${rgb}, 0.35) !important;
        }
        
        /* Status badge colors */
        .hdr-credits-badge {
            color: ${branding.primaryColor} !important;
            background: rgba(${rgb}, 0.16) !important;
            border-color: rgba(${rgb}, 0.35) !important;
        }
        
        /* Links and text accents */
        .lp-user-menu-meta-link,
        .link-branding-accent {
            color: ${branding.primaryColor} !important;
        }
        
        .lp-user-menu-meta-link:hover {
            color: ${branding.primaryColor}dd !important;
        }
    `;

    document.head.appendChild(styleTag);
    console.log('[BrandingCssInjector] CSS injected with primaryColor:', branding.primaryColor);
}

/**
 * Supprime les styles CSS personnalisés
 */
export function removeBrandingCss(): void {
    const styleTag = document.getElementById(BRANDING_STYLE_ID);
    if (styleTag) {
        styleTag.remove();
        console.log('[BrandingCssInjector] Custom branding CSS removed');
    }
}

/**
 * Met à jour le favicon
 */
export function updateFavicon(url: string): void {
    if (!url) return;

    try {
        // Trouver ou créer le link favicon
        let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (!favicon) {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            document.head.appendChild(favicon);
        }
        favicon.href = url;

        // Mettre à jour aussi les autres tailles de favicon si présentes
        const faviconSizes = document.querySelectorAll('link[rel="icon"][sizes]');
        faviconSizes.forEach((link) => {
            (link as HTMLLinkElement).href = url;
        });

        // Mettre à jour l'apple-touch-icon si présent
        const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
        if (appleTouchIcon) {
            appleTouchIcon.href = url;
        }

        console.log('[BrandingCssInjector] Favicon updated to:', url);
    } catch (error) {
        console.error('[BrandingCssInjector] Error updating favicon:', error);
    }
}

/**
 * Restaure le favicon par défaut
 */
export function restoreDefaultFavicon(): void {
    try {
        const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (favicon) {
            favicon.href = ORIGINAL_FAVICON_HREF;
        }
        console.log('[BrandingCssInjector] Favicon restored to default');
    } catch (error) {
        console.error('[BrandingCssInjector] Error restoring favicon:', error);
    }
}

/**
 * Met à jour le titre du document
 */
export function updateDocumentTitle(appName: string): void {
    if (!appName) return;

    try {
        // Récupérer le titre actuel pour conserver le format "Page - AppName"
        const currentTitle = document.title;
        const separator = ' - ';
        
        if (currentTitle.includes(separator)) {
            // Format existant "Page - Famla" → "Page - NewAppName"
            const parts = currentTitle.split(separator);
            if (parts.length > 1) {
                parts[parts.length - 1] = appName;
                document.title = parts.join(separator);
            }
        } else {
            // Titre simple initial → nom du tenant
            document.title = appName;
        }
        
        console.log('[BrandingCssInjector] Document title updated to include:', appName);
    } catch (error) {
        console.error('[BrandingCssInjector] Error updating document title:', error);
    }
}

/**
 * Restaure le titre par défaut
 */
export function restoreDefaultTitle(): void {
    try {
        const currentTitle = document.title;
        const separator = ' - ';
        
        if (currentTitle.includes(separator)) {
            const parts = currentTitle.split(separator);
            if (parts.length > 1) {
                parts[parts.length - 1] = ORIGINAL_TITLE;
                document.title = parts.join(separator);
            }
        } else {
            document.title = ORIGINAL_TITLE;
        }
        
        console.log('[BrandingCssInjector] Document title restored to default');
    } catch (error) {
        console.error('[BrandingCssInjector] Error restoring document title:', error);
    }
}

export default {
    injectBrandingCss,
    removeBrandingCss,
    updateFavicon,
    restoreDefaultFavicon,
    updateDocumentTitle,
    restoreDefaultTitle
};
